/**
 * What the monitoring timeline draws for one stop: where its box sits, which times it shows, and
 * which of the editing affordances it gets. Everything here is a function of the stop, its route
 * and the clock, so the stop component only has to render the answer.
 */
import { differenceInMinutes } from "date-fns"

import { getStopTimeVariance } from "@/lib/statusDisplay"
import { hasStopDeparted, isRouteOver } from "@/lib/stopTiming"
import { timeToPosition } from "@/lib/timelineScale"
import type { Route, Stop } from "@/types/collections"

export interface StopPresentationContext {
  /** The stop's position in `route.stops`, the order the row draws them in. */
  index: number
  isPastDate: boolean
  currentTime: Date
  /** The day on screen and the zoom, for placing the box on the timeline. */
  selectedDate: Date
  hourWidth: number
}

export interface StopPresentation {
  isSkipped: boolean
  hasDeparted: boolean
  /** A stop the route finished without either system ever recording. */
  unaccounted: boolean
  /** Nothing about this stop is in the future, whether or not it was recorded. */
  settled: boolean
  /** The first stop still ahead of the van on a route that is running. */
  isNextStop: boolean
  /** Today, route unlocked, stop not settled: laid out by plan so a drag is stable. */
  isEditableStop: boolean
  /** Route unlocked and stop not settled: the box carries drag handles and a delete button. */
  showsEditControls: boolean
  /** The time the box's left edge stands for. */
  arrivalTime: Date
  /** The time the box's right edge stands for. */
  departureTime: Date
  startPos: number
  endPos: number
  width: number
  /** Arrived and still there; the box grows with the clock from here. */
  onSiteSince: Date | null
  departureUnrecorded: boolean
  /** Minutes between recorded arrival and departure, for a departed stop. */
  durationOnSite: number | null
  hasActual: boolean
  /** Behind the van with no time recorded either side of it: shown as a dash, not the plan. */
  timeUnrecorded: boolean
  /** A live ETA that differs from the plan, so the plan is printed beside it. */
  etaDiffers: boolean
  /** Delay from the ETA where there is no actual arrival yet, else the stop's own delay. */
  calculatedDelayMinutes: number | undefined
  /** For past dates: actual drift from plan, through the shared definition. */
  actualDelayMinutes: number | null
  /** For past dates: whether the recorded arrival was 5+ minutes either side of the plan. */
  pastArrivalRing: "late" | "early" | null
}

export function deriveStopPresentation(
  stop: Stop,
  route: Route,
  ctx: StopPresentationContext
): StopPresentation {
  const { index, isPastDate, currentTime, selectedDate, hourWidth } = ctx

  const isSkipped = stop.status === "skipped"
  const hasDeparted = hasStopDeparted(stop)

  /**
   * The run is over: nothing on this route is still ahead of the van.
   *
   * Read off the *resolved* route status, so a route the driver closed in
   * Track-POD counts as finished even where Samsara's depot geofence never
   * fired -- which is the case this distinction exists for.
   */
  const routeIsOver = isRouteOver(route)
  /**
   * A stop the route finished without either system ever recording.
   *
   * Not the same as done, and not the same as pending. `hasDeparted` answers
   * "is this stop recorded as finished", and the timeline was using it for a
   * second question it cannot answer -- "is this stop still to come" -- so a
   * stop with no record sat in full-strength collection colour, in the row of a
   * route whose header said Completed, looking like the next thing the driver
   * would do. These are the stops with an order in Track-POD and no driver
   * status against them: the ones worth chasing, which is a reason to make them
   * legible as unresolved, not a reason to render them as upcoming.
   */
  const unaccounted = routeIsOver && !hasDeparted && !isSkipped
  /**
   * Nothing about this stop is in the future, whether or not it was recorded.
   *
   * The condition for everything the timeline says about a stop the van has
   * moved past: greyed out, not draggable, no ETA, no delay badge, and never
   * the blue "next stop" of a route that has finished.
   */
  const settled = hasDeparted || unaccounted

  // The NEXT stop is the first non-departed, non-skipped stop
  const isNextStop =
    !routeIsOver && !hasDeparted && route.stops.slice(0, index).every(hasStopDeparted)

  // Choose which time source to use for layout:
  // - When editable (today, route unlocked, stop not departed): always layout by planned times so
  //   dragging/resizing feels stable and isn't affected by live ETAs.
  // - Otherwise (past dates, locked routes, departed/skipped stops): show "what actually happened /
  //   is expected" by prioritising actual > ETA > planned.
  const isEditableStop = !route.isLocked && !isPastDate && !settled

  /**
   * An unaccounted stop is drawn where it was planned.
   *
   * It has no actuals by definition, and the remaining fallback is an ETA left
   * over from a route that has since finished -- a prediction of an arrival
   * that is now in the past and was never observed. The plan is at least an
   * honest statement of where the box belongs.
   */
  const layoutByPlan = isEditableStop || unaccounted

  const arrivalTime = layoutByPlan
    ? stop.plannedArrival
    : stop.actualArrival || stop.estimatedArrival || stop.plannedArrival

  /**
   * A van standing at a stop has no departure time, but its box still needs a
   * right edge -- so the box grows with the clock for as long as it stands
   * there. An extent is not a claim: it renders the overrun without asserting
   * an end that nobody knows yet. See DepartureProjection.
   */
  const departureProjection = stop.departureProjection
  // `settled` is checked as well as the projection because the resolver judges
  // each stop alone: it cannot see that the route finished hours ago, which is
  // the other way a stop comes to look like it is still being serviced.
  const onSiteSince =
    departureProjection?.kind === "on_site" && !settled ? departureProjection.since : null
  const departureUnrecorded = departureProjection?.kind === "unrecorded"

  const departureTime = layoutByPlan
    ? stop.plannedDeparture
    : onSiteSince
      ? new Date(Math.max(currentTime.getTime(), stop.plannedDeparture.getTime()))
      : stop.actualDeparture || stop.estimatedDeparture || stop.plannedDeparture

  const startPos = timeToPosition(arrivalTime, selectedDate, hourWidth)
  const endPos = timeToPosition(departureTime, selectedDate, hourWidth)
  const width = Math.max(endPos - startPos, 30)

  const durationOnSite =
    hasDeparted && stop.actualArrival && stop.actualDeparture
      ? differenceInMinutes(stop.actualDeparture, stop.actualArrival)
      : null

  const hasETA = stop.estimatedArrival !== undefined
  const hasActual = stop.actualArrival !== undefined
  /**
   * A stop the route has moved past with no time recorded either side of it.
   *
   * Every fallback here ends at the planned time, which is the right answer for a stop still
   * ahead of the van -- the plan is the best available reply to "when should I expect this" --
   * and the wrong one once the stop is behind it. A stop resolved as done by the driver's order
   * status, or deduced from the stops either side of it, has genuinely happened and genuinely
   * has no clock reading: printing 04:45 in the same ink as a measured time, in a box greyed out
   * to say "done", states an arrival nobody observed. These rows show a dash. Same rule as the
   * stop list in the route panel.
   *
   * Covers the unaccounted stops too, for the stronger version of the same reason: there the
   * plan would be the only time on the row, standing in for a record nobody has.
   */
  const timeUnrecorded = settled && !stop.actualArrival && !stop.actualDeparture
  const etaDiffers =
    !hasActual && hasETA && stop.estimatedArrival!.getTime() !== stop.plannedArrival.getTime()

  let calculatedDelayMinutes = stop.delayMinutes
  if (!hasActual && hasETA && stop.estimatedArrival) {
    const delayMs = stop.estimatedArrival.getTime() - stop.plannedArrival.getTime()
    calculatedDelayMinutes = Math.round(delayMs / (1000 * 60))
  }

  const actualDelayMinutes = isPastDate
    ? (getStopTimeVariance(stop.plannedArrival, stop.actualArrival)?.minutes ?? null)
    : null

  let pastArrivalRing: StopPresentation["pastArrivalRing"] = null
  if (isPastDate && stop.actualArrival) {
    const delayMs = stop.actualArrival.getTime() - stop.plannedArrival.getTime()
    const delayMinutes = Math.round(delayMs / (1000 * 60))
    if (delayMinutes >= 5) pastArrivalRing = "late"
    else if (delayMinutes <= -5) pastArrivalRing = "early"
  }

  return {
    isSkipped,
    hasDeparted,
    unaccounted,
    settled,
    isNextStop,
    isEditableStop,
    showsEditControls: !route.isLocked && !settled,
    arrivalTime,
    departureTime,
    startPos,
    endPos,
    width,
    onSiteSince,
    departureUnrecorded,
    durationOnSite,
    hasActual,
    timeUnrecorded,
    etaDiffers,
    calculatedDelayMinutes,
    actualDelayMinutes,
    pastArrivalRing,
  }
}

/**
 * The span of the travel line drawn into a stop from the one before it.
 *
 * For editable routes the line lines up with the planned schedule (what is being edited), not the
 * live ETA. For historical or locked routes it shows the real leg, from actual/ETA where known.
 */
export function legSpan(
  prevStop: Stop,
  stop: Stop,
  route: Route,
  isPastDate: boolean,
  isEditableStop: boolean
): { from: Date; to: Date } {
  const prevHasDeparted =
    prevStop.status === "completed" ||
    prevStop.status === "skipped" ||
    prevStop.actualDeparture !== undefined
  const isPrevEditable = !route.isLocked && !isPastDate && !prevHasDeparted

  const from = isPrevEditable
    ? prevStop.plannedDeparture
    : prevStop.actualDeparture || prevStop.estimatedDeparture || prevStop.plannedDeparture
  const to = isEditableStop
    ? stop.plannedArrival
    : stop.actualArrival || stop.estimatedArrival || stop.plannedArrival
  return { from, to }
}
